/* CRMEventiPage.jsx — Admin gestione eventi (lista iscritti, QR, stats, export) */
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  PartyPopper, Download, QrCode, TrendingUp, Users, Loader2,
  RefreshCw, Power, PowerOff, Save, Upload, Sparkles, Image as ImageIcon,
  CheckCircle2, AlertTriangle, Bell, BellRing, Send,
} from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/eventi`;

export default function CRMEventiPage() {
  const [eventi, setEventi] = useState([]);
  const [selSlug, setSelSlug] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("lista"); // "lista" | "ai-upload"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(BASE, { withCredentials: true });
      setEventi(data.items || []);
      if (data.items?.length && !selSlug) setSelSlug(data.items[0].slug);
    } catch { toast.error("Errore caricamento eventi"); }
    finally { setLoading(false); }
  }, [selSlug]);

  useEffect(() => { load(); }, [load]);

  const sel = eventi.find((e) => e.slug === selSlug);

  return (
    <div data-testid="crm-eventi-page" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <PartyPopper className="w-6 h-6 text-[#D97B58]" />
          <h2 className="text-xl font-bold text-[#163321]">Eventi Promozionali</h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setMode("lista")}
            data-testid="tab-eventi-lista"
            className={`px-4 py-2 rounded-full text-sm font-bold border-2 transition-all ${
              mode === "lista"
                ? "bg-[#163321] text-white border-[#163321]"
                : "bg-white text-slate-600 border-slate-200"
            }`}
          >
            Lista Eventi
          </button>
          <button
            onClick={() => setMode("ai-upload")}
            data-testid="tab-eventi-ai-upload"
            className={`px-4 py-2 rounded-full text-sm font-bold border-2 transition-all inline-flex items-center gap-1.5 ${
              mode === "ai-upload"
                ? "bg-amber-500 text-white border-amber-500"
                : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50"
            }`}
          >
            <Sparkles className="w-4 h-4" /> Carica Locandina (AI)
          </button>
        </div>
      </div>

      {mode === "ai-upload" ? (
        <AIPosterUpload onCreated={() => { setMode("lista"); load(); }} />
      ) : loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {eventi.map((e) => (
              <button
                key={e.slug}
                onClick={() => setSelSlug(e.slug)}
                data-testid={`evento-tab-${e.slug}`}
                className={[
                  "px-4 py-2 rounded-full text-sm font-medium border-2",
                  selSlug === e.slug
                    ? "bg-[#163321] text-white border-[#163321]"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400",
                ].join(" ")}
              >
                {e.titolo} {!e.attivo && <span className="text-xs opacity-70">(off)</span>}
              </button>
            ))}
          </div>

          {sel && <EventoDetail evento={sel} onChange={load} />}
        </>
      )}
    </div>
  );
}

function EventoDetail({ evento, onChange }) {
  const [edit, setEdit] = useState(evento);
  const [stats, setStats] = useState(null);
  const [iscritti, setIscritti] = useState([]);
  const [iscQuery, setIscQuery] = useState("");
  const [iscLoading, setIscLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [qrUtm, setQrUtm] = useState("stand_principale");

  useEffect(() => {
    setEdit(evento);
    axios.get(`${BASE}/${evento.slug}/stats`, { withCredentials: true })
      .then((r) => setStats(r.data))
      .catch(() => setStats(null));
    loadIscritti();
  }, [evento.slug]);

  const loadIscritti = async () => {
    setIscLoading(true);
    try {
      const { data } = await axios.get(`${BASE}/${evento.slug}/iscritti`,
        { params: iscQuery ? { q: iscQuery } : {},
          withCredentials: true });
      setIscritti(data.items || []);
    } catch { setIscritti([]); }
    finally { setIscLoading(false); }
  };

  const saveEvento = async () => {
    setSaving(true);
    try {
      const body = { ...edit };
      delete body.id; delete body.iscritti_count;
      delete body.qr_codes_generati; delete body.created_at;
      delete body.updated_at; delete body.created_by_admin_id;
      await axios.put(`${BASE}/${evento.slug}`, body,
        { withCredentials: true });
      toast.success("Evento aggiornato");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Errore");
    } finally { setSaving(false); }
  };

  const toggleAttivo = async () => {
    setEdit((s) => ({ ...s, attivo: !s.attivo }));
    setTimeout(saveEvento, 100);
  };

  const downloadCsv = () => {
    window.open(`${BASE}/${evento.slug}/export.csv`, "_blank");
  };

  const downloadQr = () => {
    const url = `${BASE}/${evento.slug}/qr-code?utm_source=${encodeURIComponent(qrUtm)}`;
    window.open(url, "_blank");
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      {/* COLONNA 1: configurazione */}
      <div className="lg:col-span-1 space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="text-sm font-bold text-[#163321] mb-3">Configurazione</h3>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs">Titolo</Label>
              <Input value={edit.titolo}
                onChange={(e) => setEdit({ ...edit, titolo: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Sottotitolo</Label>
              <Input value={edit.sottotitolo || ""}
                onChange={(e) => setEdit({ ...edit, sottotitolo: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Incentivo (premio)</Label>
              <Input value={edit.incentivo || ""}
                onChange={(e) => setEdit({ ...edit, incentivo: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Luogo</Label>
              <Input value={edit.luogo || ""}
                onChange={(e) => setEdit({ ...edit, luogo: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Inizio</Label>
                <Input type="date" value={edit.data_inizio || ""}
                  onChange={(e) => setEdit({ ...edit, data_inizio: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Fine</Label>
                <Input type="date" value={edit.data_fine || ""}
                  onChange={(e) => setEdit({ ...edit, data_fine: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Banner text (homepage)</Label>
              <Input value={edit.banner_text || ""}
                onChange={(e) => setEdit({ ...edit, banner_text: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Banner color (hex)</Label>
              <Input value={edit.banner_color || ""}
                onChange={(e) => setEdit({ ...edit, banner_color: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-xs">
              <Checkbox checked={!!edit.consenso_concorso_required}
                onCheckedChange={(c) => setEdit({ ...edit,
                  consenso_concorso_required: !!c })} />
              Richiedi consenso concorso
            </label>
            <Button onClick={saveEvento} disabled={saving}
                     className="w-full mt-2 bg-[#163321]"
                     data-testid="evento-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" />
                : <><Save className="w-4 h-4 mr-1" /> Salva</>}
            </Button>
            <Button onClick={toggleAttivo} variant="outline"
                     className="w-full"
                     data-testid="evento-toggle-attivo">
              {edit.attivo
                ? <><PowerOff className="w-4 h-4 mr-1" /> Disattiva evento</>
                : <><Power className="w-4 h-4 mr-1" /> Attiva evento</>}
            </Button>
          </div>
        </div>

        {/* QR code */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="text-sm font-bold text-[#163321] mb-3 flex items-center gap-2">
            <QrCode className="w-4 h-4" /> QR Code per stand
          </h3>
          <div className="space-y-2 text-sm">
            <div>
              <Label className="text-xs">UTM source (nome stand)</Label>
              <Input value={qrUtm}
                onChange={(e) => setQrUtm(e.target.value)}
                placeholder="es. stand_ingresso, totem_b" />
            </div>
            <Button onClick={downloadQr} className="w-full"
                     data-testid="evento-qr-download">
              <Download className="w-4 h-4 mr-1" /> Scarica QR
            </Button>
            <img
              alt="QR preview"
              src={`${BASE}/${evento.slug}/qr-code?utm_source=${encodeURIComponent(qrUtm)}`}
              className="w-full rounded-xl border border-slate-200 mt-2 bg-white"
              key={qrUtm}
              data-testid="evento-qr-preview"
            />
          </div>
        </div>
      </div>

      {/* COLONNA 2-3: stats + iscritti */}
      <div className="lg:col-span-2 space-y-4">
        {/* WIDGET PUSH DISPATCH (TASK 7.2) */}
        <PushDispatchWidget evento={evento} />
        {/* KPI */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Iscritti totali"
                value={stats?.iscritti_totali ?? "—"}
                icon={Users} />
          <Kpi label="Ultimi 7 gg"
                value={stats?.iscritti_ultimi_7gg ?? "—"}
                icon={TrendingUp} />
          <Kpi label="Account creati"
                value={stats?.registrazioni_area_cliente ?? "—"}
                icon={Users} />
          <Kpi label="Conversion"
                value={stats ? `${stats.conversion_rate_percent}%` : "—"}
                icon={TrendingUp} />
        </div>
        {/* Top prodotti */}
        {stats?.top_prodotti?.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
              Top prodotti richiesti
            </h4>
            <div className="flex flex-wrap gap-2">
              {stats.top_prodotti.map((p) => (
                <Badge key={p.prodotto} variant="outline" className="text-xs">
                  {p.prodotto} · {p.n}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Lista iscritti */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <h3 className="font-bold text-[#163321] text-sm">Iscritti</h3>
            <div className="flex gap-2 items-center">
              <Input placeholder="Cerca..." value={iscQuery}
                onChange={(e) => setIscQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadIscritti()}
                className="h-8 w-44 text-xs" />
              <Button size="sm" variant="outline" onClick={loadIscritti}>
                <RefreshCw className="w-3 h-3" />
              </Button>
              <Button size="sm" onClick={downloadCsv}
                       className="bg-[#163321]"
                       data-testid="evento-export-csv">
                <Download className="w-3 h-3 mr-1" /> CSV
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2">Data</th>
                  <th className="text-left py-2">Nome</th>
                  <th className="text-left py-2">Email</th>
                  <th className="text-left py-2">Telefono</th>
                  <th className="text-left py-2">Interessi</th>
                  <th className="text-left py-2">UTM</th>
                </tr>
              </thead>
              <tbody>
                {iscLoading ? (
                  <tr><td colSpan={6} className="text-center py-6 text-slate-400">
                    <Loader2 className="w-4 h-4 animate-spin inline" />
                  </td></tr>
                ) : iscritti.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-6 text-slate-400">
                    Nessun iscritto ancora.
                  </td></tr>
                ) : iscritti.map((i) => (
                  <tr key={i.id}
                       data-testid={`evento-iscritto-${i.id}`}
                       className="border-b border-slate-100">
                    <td className="py-2 text-slate-500">
                      {i.created_at?.slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="py-2 font-medium">{i.nome} {i.cognome}</td>
                    <td className="py-2 text-slate-600">{i.email}</td>
                    <td className="py-2 font-mono text-slate-600">{i.telefono}</td>
                    <td className="py-2">
                      {(i.interesse_prodotti || []).slice(0, 3).map((p) => (
                        <span key={p} className="inline-block mr-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{p}</span>
                      ))}
                    </td>
                    <td className="py-2 text-slate-400 text-[10px]">
                      {i.utm_source || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-slate-400" />
        <span className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">{label}</span>
      </div>
      <p className="text-xl font-black text-[#163321]">{value}</p>
    </div>
  );
}


/* ============================================================
 * PushDispatchWidget — counter push inviate per evento (TASK 7.2)
 * ============================================================ */
function PushDispatchWidget({ evento }) {
  const [sending, setSending] = useState(false);
  const [dispatch, setDispatch] = useState(evento?.push_dispatch || null);

  const resendPush = async () => {
    if (!window.confirm(
      "Vuoi inviare nuovamente la notifica push agli iscritti Area Cliente con residenza in " +
      (evento.citta || "—") + "?\n\nThrottle 24h: chi ha gia' ricevuto la push per questo evento non riceve duplicati.",
    )) return;
    setSending(true);
    try {
      const { data } = await axios.post(
        `${BASE}/${evento.slug}/send-push-now`, {},
        { withCredentials: true },
      );
      setDispatch(data);
      toast.success(
        `Push inviate: ${data.sent_total} / ${data.active_accounts} account attivi`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Errore invio push");
    } finally { setSending(false); }
  };

  const hasDispatch = dispatch && dispatch.ts;

  return (
    <div
      className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-4"
      data-testid="evento-push-dispatch-widget"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center">
            <BellRing className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h4 className="font-bold text-[#163321] text-sm">
              📲 Notifiche push automatiche
            </h4>
            <p className="text-xs text-slate-600">
              Topic <code className="bg-white px-1 rounded">nuovi_eventi</code> · Match per residenza
            </p>
          </div>
        </div>
        <Button
          onClick={resendPush}
          disabled={sending || !evento?.attivo}
          data-testid="evento-resend-push-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white h-9 text-xs"
        >
          {sending ? (
            <span className="inline-flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Invio...
            </span>
          ) : (
            <span className="inline-flex items-center gap-1">
              <Send className="w-3 h-3" /> Re-invia push
            </span>
          )}
        </Button>
      </div>

      {hasDispatch ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          <PushKpi label="Subscribers raggiunti"
                    value={dispatch.sent_total ?? 0}
                    testid="push-kpi-sent" />
          <PushKpi label="Account attivi target"
                    value={dispatch.active_accounts ?? 0}
                    testid="push-kpi-accounts" />
          <PushKpi label="Cliente match"
                    value={dispatch.matched_customers ?? 0}
                    testid="push-kpi-matched" />
          <PushKpi label="No subscription"
                    value={dispatch.no_subscription ?? 0}
                    testid="push-kpi-nosub" />
        </div>
      ) : (
        <p className="text-xs text-slate-500 mt-3" data-testid="push-no-dispatch-yet">
          Nessuna push inviata ancora. Le notifiche vengono inviate automaticamente
          quando l'evento viene <b>attivato</b> per la prima volta. Puoi anche
          forzare il re-invio con il bottone sopra (rispetta throttle 24h).
        </p>
      )}

      {hasDispatch && (
        <p className="text-[10px] text-slate-400 mt-3" data-testid="push-dispatch-ts">
          Ultimo invio: {new Date(dispatch.ts).toLocaleString("it-IT")}
          {dispatch.trigger && ` · trigger: ${dispatch.trigger}`}
          {dispatch.by_admin && ` · by ${dispatch.by_admin}`}
        </p>
      )}
    </div>
  );
}

function PushKpi({ label, value, testid }) {
  return (
    <div className="bg-white rounded-xl border border-blue-100 p-3"
         data-testid={testid}>
      <p className="text-2xl font-black text-blue-700">{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mt-0.5">{label}</p>
    </div>
  );
}



/* ============================================================
 * AIPosterUpload — drag&drop locandina + estrazione AI + form review
 *   - Storage permanente locandina (upload-locandina endpoint)
 *   - Descrizione marketing AI editabile + bottone Rigenera AI
 *   - Date/ore via input nativi date/time
 *   - Validazione date inizio/fine + preview live
 * ============================================================ */
function AIPosterUpload({ onCreated }) {
  const [mode, setMode] = useState("locandina"); // v1.14.62.0: "locandina" | "url"
  const [urlInput, setUrlInput] = useState("");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confidence, setConfidence] = useState(null);
  const [provider, setProvider] = useState(null);
  const [form, setForm] = useState(null);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const pickFile = (f) => {
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(f.type)) {
      toast.error("Formato non supportato: usa PNG, JPG o WEBP");
      return;
    }
    setFile(f);
    setForm(null);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    pickFile(e.dataTransfer.files?.[0]);
  };

  const extract = async () => {
    if (!file) { toast.error("Seleziona prima una locandina"); return; }
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(
        `${BASE}/extract-poster`, fd,
        { withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" } },
      );
      applyExtractedData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message
                   || "Errore estrazione AI");
    } finally { setExtracting(false); }
  };

  const extractFromUrl = async () => {
    if (!urlInput.trim()) { toast.error("Inserisci un URL"); return; }
    if (!/^https?:\/\/.+/i.test(urlInput.trim())) {
      toast.error("URL non valido (deve iniziare con http:// o https://)");
      return;
    }
    setExtracting(true);
    try {
      const { data } = await axios.post(
        `${BASE}/extract-from-url`,
        { url: urlInput.trim() },
        { withCredentials: true },
      );
      applyExtractedData(data);
      if (data.from_cache) {
        toast.info("Risultato dalla cache (estrazione già fatta in passato)");
      }
    } catch (e) {
      const det = e.response?.data?.detail;
      const code = det?.error_code;
      if (code === "AI_UNAVAILABLE") {
        toast.error("AI temporaneamente non disponibile. Usa la locandina o compila a mano.");
      } else if (code === "URL_NOT_ALLOWED") {
        toast.error(`URL non ammesso: ${det.message || "schema/host non valido"}`);
      } else if (code === "RATE_LIMIT_EXCEEDED") {
        toast.error("Limite estrazioni/ora raggiunto. Riprova tra poco.");
      } else if (code === "EXTRACTION_PARSE_FAILED") {
        toast.error("AI non ha restituito un formato valido. Compila i campi a mano.");
      } else {
        toast.error(det?.message || "Errore estrazione da link");
      }
    } finally { setExtracting(false); }
  };

  const applyExtractedData = (data) => {
    setConfidence(data.ai_extraction_confidence);
    setProvider(data.provider);
    const d = data.data || {};
    setForm({
      slug: data.slug_suggest || "",
      titolo: d.titolo_evento || "",
      sottotitolo: d.descrizione || "",
      descrizione_marketing: d.descrizione_marketing || "",
      incentivo: d.incentivo_offerto || "",
      data_inizio: d.data_inizio || "",
      data_fine: d.data_fine || "",
      ora_inizio: d.ora_inizio || "",
      ora_fine: d.ora_fine || "",
      luogo: d.luogo || "",
      indirizzo_completo: d.indirizzo_completo || "",
      citta: d.citta || "",
      provincia: d.provincia || "",
      organizzatore: d.organizzatore || "",
      contatti_evento: d.contatti_evento || "",
      tipo_evento: d.tipo_evento || "",
      attivo: true,
      banner_text: d.titolo_evento ? `🎉 ${d.titolo_evento}` : "",
      banner_color: "#D97B58",
      prodotti_interesse_disponibili: ["Auto", "Casa", "Vita", "Salute",
                                        "Aziende", "Viaggio", "Altro"],
      consenso_concorso_required: (d.tipo_evento === "concorso"),
      ai_extracted_data: d,
      ai_extraction_confidence: data.ai_extraction_confidence,
      ai_extraction_provider: data.provider,
      source_url: data.source_url || null,
      image_url_hint: d.image_url || null,
    });
    toast.success(`Dati estratti (confidence ${(data.ai_extraction_confidence * 100).toFixed(0)}%)`);
  };

  const regenDesc = async () => {
    if (!form?.titolo) {
      toast.error("Inserisci almeno il titolo");
      return;
    }
    setRegenerating(true);
    try {
      const { data } = await axios.post(
        `${BASE}/regen-description`,
        {
          titolo: form.titolo,
          tipo_evento: form.tipo_evento,
          data_inizio: form.data_inizio,
          ora_inizio: form.ora_inizio,
          luogo: form.luogo,
          citta: form.citta,
          organizzatore: form.organizzatore,
          incentivo: form.incentivo,
        },
        { withCredentials: true },
      );
      setForm((f) => ({ ...f, descrizione_marketing: data.descrizione_marketing }));
      toast.success("Nuova descrizione generata");
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Errore generazione AI");
    } finally { setRegenerating(false); }
  };

  const dateError = (() => {
    if (!form?.data_inizio || !form?.data_fine) return null;
    return form.data_fine < form.data_inizio
      ? "La data di fine deve essere dopo l'inizio" : null;
  })();
  const datePastWarning = (() => {
    if (!form?.data_inizio) return null;
    const today = new Date().toISOString().slice(0, 10);
    return form.data_inizio < today
      ? "Stai pubblicando un evento gia' iniziato/passato. Procedi solo se e' uno storico." : null;
  })();

  // COMPLIANCE DPR 430/2001 — linter live parole vietate
  const complianceLint = (() => {
    if (!form) return { has_violations: false, matches: [] };
    const FORBIDDEN = /\b(vinci|vincere|vincitor[ei]|vincita|concors[oi]|premi[oi]?|estrazion[ei]|in\s+palio|sorteggi[oi]?|giocar[ei]|montepremi|vincente)\b/gi;
    const fields = [
      ["titolo", form.titolo],
      ["sottotitolo", form.sottotitolo],
      ["descrizione_marketing", form.descrizione_marketing],
      ["incentivo", form.incentivo],
      ["banner_text", form.banner_text],
    ];
    const matches = [];
    for (const [field, txt] of fields) {
      if (!txt) continue;
      for (const m of String(txt).matchAll(FORBIDDEN)) {
        matches.push({ field, word: m[0] });
      }
    }
    return { has_violations: matches.length > 0, matches };
  })();

  const save = async () => {
    if (!form?.titolo || !form?.slug) {
      toast.error("Titolo e slug obbligatori");
      return;
    }
    if (dateError) {
      toast.error(dateError);
      return;
    }
    if (form.descrizione_marketing
        && form.descrizione_marketing.length < 50) {
      toast.error("La descrizione deve avere almeno 50 caratteri");
      return;
    }
    setSaving(true);
    try {
      // STEP 1: upload locandina su storage permanente
      let locandina_url = null;
      if (file) {
        const fdLoc = new FormData();
        fdLoc.append("file", file);
        const respLoc = await axios.post(
          `${BASE}/upload-locandina?slug=${encodeURIComponent(form.slug)}`,
          fdLoc,
          { withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" } },
        );
        // URL e' relativo /api/public/eventi/locandine/... → assoluto per frontend
        locandina_url = `${process.env.REACT_APP_BACKEND_URL}${respLoc.data.url}`;
      }
      // STEP 2: crea evento
      const body = {
        ...form,
        locandina_url,
        descrizione_html: form.descrizione_marketing
          ? `<p>${form.descrizione_marketing.split("\n\n").join("</p><p>")}</p>`
          : "",
      };
      delete body.descrizione_marketing; // server-side non l'aspetta come campo flat
      await axios.post(BASE, body, { withCredentials: true });
      toast.success("Evento pubblicato!");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message
                   || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const formatDateLabel = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("it-IT",
        { day: "numeric", month: "long", year: "numeric" });
    } catch { return iso; }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 space-y-5"
         data-testid="ai-poster-upload-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <h3 className="font-bold text-[#163321] text-lg">Estrai dati evento con AI</h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Carica una locandina o incolla il link dell&apos;evento — l&apos;AI estrae dati e descrizione marketing.
          </p>
        </div>
      </div>

      {/* v1.14.62.0 — Toggle modalità di estrazione */}
      <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-xl"
           data-testid="ai-poster-mode-toggle">
        <button
          type="button"
          onClick={() => setMode("locandina")}
          data-testid="ai-poster-mode-locandina"
          className={[
            "flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all",
            mode === "locandina"
              ? "bg-white shadow text-amber-700"
              : "text-slate-500 hover:text-slate-700",
          ].join(" ")}
        >
          📷 Da locandina
        </button>
        <button
          type="button"
          onClick={() => setMode("url")}
          data-testid="ai-poster-mode-url"
          className={[
            "flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all",
            mode === "url"
              ? "bg-white shadow text-amber-700"
              : "text-slate-500 hover:text-slate-700",
          ].join(" ")}
        >
          🔗 Da link
        </button>
      </div>

      {mode === "url" ? (
        <div className="space-y-3" data-testid="ai-poster-url-mode">
          <label className="block">
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">
              URL evento (Eventbrite, Facebook Events, sito organizzatore...)
            </span>
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              data-testid="ai-poster-url-input"
              placeholder="https://www.eventbrite.it/e/..."
              className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 text-sm"
            />
          </label>
          <p className="text-xs text-slate-500">
            Solo URL pubblici (HTTP/HTTPS). Max 20 estrazioni/ora. Cache 30 giorni.
          </p>
          <Button
            onClick={extractFromUrl}
            disabled={!urlInput.trim() || extracting}
            data-testid="ai-poster-extract-url-btn"
            className="w-full min-h-[52px] bg-amber-500 hover:bg-amber-600 text-white font-bold"
          >
            {extracting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" /> Estrazione in corso...
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <Sparkles className="w-5 h-5" /> Estrai dati dal link
              </span>
            )}
          </Button>
        </div>
      ) : (
        <>
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            data-testid="ai-poster-dropzone"
            className={[
              "border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-colors",
              drag
                ? "border-amber-500 bg-amber-50"
                : "border-slate-300 hover:border-amber-400 bg-slate-50",
            ].join(" ")}
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              data-testid="ai-poster-file-input"
              onChange={(e) => pickFile(e.target.files?.[0])}
            />
            {previewUrl ? (
              <div className="flex flex-col items-center gap-3">
                <img src={previewUrl} alt="preview locandina"
                     className="max-h-48 rounded-xl border border-slate-200 shadow-md"
                     data-testid="ai-poster-preview" />
                <p className="text-xs text-slate-500">{file.name} · {Math.round(file.size / 1024)}KB</p>
              </div>
            ) : (
              <>
                <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                <p className="text-sm font-bold text-slate-700">Trascina qui la locandina</p>
                <p className="text-xs text-slate-500 mt-1">
                  oppure clicca per selezionare (PNG, JPG, WEBP — max 8MB)
                </p>
              </>
            )}
          </div>

          <Button
            onClick={extract}
            disabled={!file || extracting}
            data-testid="ai-poster-extract-btn"
            className="w-full min-h-[52px] bg-amber-500 hover:bg-amber-600 text-white font-bold"
          >
            {extracting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" /> Estrazione in corso...
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <Sparkles className="w-5 h-5" /> Estrai dati con AI
              </span>
            )}
          </Button>
        </>
      )}

      {form && (
        <>
          <div
            data-testid="ai-poster-confidence-badge"
            className={[
              "flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-bold",
              confidence >= 0.8
                ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                : confidence >= 0.5
                ? "bg-amber-50 border-amber-300 text-amber-700"
                : "bg-rose-50 border-rose-300 text-rose-700",
            ].join(" ")}
          >
            {confidence >= 0.5
              ? <CheckCircle2 className="w-5 h-5" />
              : <AlertTriangle className="w-5 h-5" />}
            Confidence AI: {(confidence * 100).toFixed(0)}% · {provider}
          </div>

          {/* Date inputs nativi + preview live */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-3">
            <Label className="text-sm font-bold text-[#163321] block">
              📅 Date e orari evento
            </Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <DateField label="Data inizio" tid="data_inizio" type="date"
                value={form.data_inizio}
                onChange={(v) => setForm({ ...form, data_inizio: v })} />
              <DateField label="Ora inizio" tid="ora_inizio" type="time"
                value={form.ora_inizio}
                onChange={(v) => setForm({ ...form, ora_inizio: v })} />
              <DateField label="Data fine" tid="data_fine" type="date"
                value={form.data_fine}
                onChange={(v) => setForm({ ...form, data_fine: v })} />
              <DateField label="Ora fine" tid="ora_fine" type="time"
                value={form.ora_fine}
                onChange={(v) => setForm({ ...form, ora_fine: v })} />
            </div>
            {form.data_inizio && (
              <p className="text-sm text-slate-700 bg-white rounded-lg px-3 py-2 border border-slate-200"
                 data-testid="ai-poster-date-preview">
                Evento dal <b>{formatDateLabel(form.data_inizio)}</b>
                {form.ora_inizio && <> alle <b>{form.ora_inizio}</b></>}
                {form.data_fine && form.data_fine !== form.data_inizio &&
                  <> al <b>{formatDateLabel(form.data_fine)}</b></>}
                {form.ora_fine && <> alle <b>{form.ora_fine}</b></>}
              </p>
            )}
            {dateError && (
              <p className="text-rose-600 text-xs font-bold flex items-center gap-1"
                 data-testid="ai-poster-date-error">
                ⚠️ {dateError}
              </p>
            )}
            {datePastWarning && !dateError && (
              <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs font-medium flex items-center gap-1"
                 data-testid="ai-poster-date-warning">
                ⚠️ {datePastWarning}
              </p>
            )}
          </div>

          {/* Descrizione marketing AI editabile */}
          <div className="bg-blue-50/40 rounded-xl p-4 border border-blue-200 space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="text-sm font-bold text-[#163321]">
                📝 Descrizione (modificabile)
              </Label>
              <Button
                type="button"
                onClick={regenDesc}
                disabled={regenerating || !form.titolo}
                data-testid="ai-poster-regen-desc-btn"
                className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white"
              >
                {regenerating ? (
                  <span className="inline-flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Rigenero...
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    🔄 Rigenera con AI
                  </span>
                )}
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              L'AI ha proposto questo testo (3-4 paragrafi). Modifica liberamente prima di pubblicare.
            </p>
            <Textarea
              value={form.descrizione_marketing || ""}
              onChange={(e) => setForm({ ...form, descrizione_marketing: e.target.value })}
              maxLength={1000}
              data-testid="ai-poster-field-descrizione_marketing"
              rows={8}
              className="text-sm leading-relaxed"
              placeholder="Descrizione marketing dell'evento..."
            />
            <p className="text-xs text-slate-400 text-right">
              {(form.descrizione_marketing || "").length} / 1000 caratteri
              {form.descrizione_marketing
                && form.descrizione_marketing.length < 50
                && <span className="text-rose-600 font-bold ml-2">⚠️ Min 50</span>}
            </p>
          </div>

          {/* Form review */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"
               data-testid="ai-poster-review-form">
            <ReviewField label="Slug URL *" tid="slug" value={form.slug}
              onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
              placeholder="es. fiera-portici-2026" />
            <ReviewField label="Titolo *" tid="titolo" value={form.titolo}
              onChange={(v) => setForm({ ...form, titolo: v })} />
            <ReviewField label="Sottotitolo" tid="sottotitolo" value={form.sottotitolo}
              onChange={(v) => setForm({ ...form, sottotitolo: v })} />
            <ReviewField label="Tipo evento" tid="tipo_evento" value={form.tipo_evento}
              onChange={(v) => setForm({ ...form, tipo_evento: v })}
              placeholder="fiera | webinar | networking" />
            <ReviewField label="Luogo" tid="luogo" value={form.luogo}
              onChange={(v) => setForm({ ...form, luogo: v })} />
            <ReviewField label="Indirizzo completo" tid="indirizzo_completo" value={form.indirizzo_completo}
              onChange={(v) => setForm({ ...form, indirizzo_completo: v })} />
            <ReviewField label="Citta" tid="citta" value={form.citta}
              onChange={(v) => setForm({ ...form, citta: v })} />
            <ReviewField label="Provincia" tid="provincia" value={form.provincia}
              onChange={(v) => setForm({ ...form, provincia: v })}
              placeholder="NA" />
            <ReviewField label="Organizzatore" tid="organizzatore" value={form.organizzatore}
              onChange={(v) => setForm({ ...form, organizzatore: v })} />
            <ReviewField label="Contatti evento" tid="contatti_evento" value={form.contatti_evento}
              onChange={(v) => setForm({ ...form, contatti_evento: v })} />
            <div className="sm:col-span-2">
              <ReviewField label="Incentivo / Premio" tid="incentivo" value={form.incentivo}
                onChange={(v) => setForm({ ...form, incentivo: v })} />
            </div>
          </div>

          {/* COMPLIANCE LINT WARNING (DPR 430/2001) */}
          {complianceLint.has_violations && (
            <div
              data-testid="ai-poster-compliance-warning"
              className="bg-rose-50 border-2 border-rose-300 rounded-xl p-4 space-y-2"
            >
              <p className="text-sm font-bold text-rose-800 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                Attenzione compliance DPR 430/2001
              </p>
              <p className="text-xs text-rose-700 leading-relaxed">
                Parole come{" "}
                {complianceLint.matches.slice(0, 5).map((m) => (
                  <span key={m.field + m.word}
                        className="inline-block bg-rose-200 px-1.5 py-0.5 rounded mx-0.5 font-bold">
                    "{m.word}" ({m.field})
                  </span>
                ))}
                potrebbero classificare l'iniziativa come <b>concorso a premi
                regolamentato</b> (autorizzazione MISE, cauzione, regolamento).
                Sostituiscile con: "gadget in omaggio", "consulenza gratuita",
                "vieni a trovarci".
              </p>
            </div>
          )}

          <Button
            onClick={save}
            disabled={saving}
            data-testid="evento-publish-btn"
            className="w-full min-h-[56px] bg-[#163321] hover:bg-[#1d4a2e] text-white font-bold text-base"
          >
            {saving ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" /> Pubblicazione...
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <Save className="w-5 h-5" /> 📢 Pubblica evento
              </span>
            )}
          </Button>
        </>
      )}
    </div>
  );
}

function DateField({ label, tid, type, value, onChange }) {
  return (
    <div>
      <Label className="text-xs font-bold text-slate-700 mb-1 block">{label}</Label>
      <Input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`ai-poster-field-${tid}`}
        className="h-10 text-sm"
      />
    </div>
  );
}

function ReviewField({ label, tid, value, onChange, placeholder }) {
  return (
    <div>
      <Label className="text-xs font-bold text-slate-700 mb-1 block">{label}</Label>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={`ai-poster-field-${tid}`}
        className="h-10 text-sm"
      />
    </div>
  );
}
